import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { PAGE_TITLES, TITLE_SUFFIX } from "./titles.js";

/**
 * Route-change behaviour, in one place so it cannot be forgotten on one route out of
 * eighteen. On every client-side navigation, in this order:
 *
 *   1. Move focus to <main id="main" tabindex="-1">.
 *   2. Announce the new page title politely.
 *   3. Reset scroll to top — except on back/forward, where position is restored.
 *
 * Hand-rolling this per screen is exactly how it gets missed, and a SPA that never moves
 * focus leaves a keyboard user at the top of the sidebar after every click.
 */
export function RouteChange() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [announcement, setAnnouncement] = useState("");
  const first = useRef(true);

  useEffect(() => {
    const title = PAGE_TITLES[location.pathname] ?? "Prompt Gateway";
    document.title = `${title}${TITLE_SUFFIX}`;

    // Skip the initial render: the browser has already placed focus and announced the page.
    if (first.current) {
      first.current = false;
      return;
    }

    document.getElementById("main")?.focus();
    setAnnouncement(title);

    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, navigationType]);

  return (
    <div className="pg-visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {announcement}
    </div>
  );
}
